import axios from 'axios'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { useUserStore } from '@/stores/user'
import { t } from '@/locales'
import { getFixedHeaders } from '@/config/headers'
import { showLoginModal } from '@/components/LoginModal/functionCall'
import { handleUserBanned } from '@/utils/banned'
import { SERVER_ERROR_CODE } from '@/common/constants'

// 创建 axios 实例
const request = axios.create({
  // API 基础路径：优先使用 VITE_AXIOS_BASE_URL（开发环境走代理），否则使用 VITE_API_BASE_URL（真实域名）
  baseURL: import.meta.env.VITE_AXIOS_BASE_URL || import.meta.env.VITE_API_BASE_URL,
  timeout: 15000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
  withCredentials: true,
})

// 请求队列（用于显示 loading）
let requestCount = 0

// 显示 loading
function showLoading() {
  if (requestCount === 0) {
    showLoadingToast({
      message: t('common.loading'),
      forbidClick: true,
      duration: 0,
    })
  }
  requestCount++
}

// 隐藏 loading
function hideLoading() {
  if (requestCount > 0) {
    requestCount--
  }
  if (requestCount === 0) {
    closeToast()
  }
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 显示 loading（可以通过配置关闭）
    if (!config.hideLoading) {
      showLoading()
    }

    // 添加固定请求头
    const fixedHeaders = getFixedHeaders()
    Object.assign(config.headers, fixedHeaders)

    // 添加 access token 和 member_id
    const userStore = useUserStore()
    const token = userStore.accessToken
    const memberId = userStore.getId()

    // 调试日志
    // console.log('[Request] URL:', config.url)
    // console.log('[Request] Access Token:', token ? `${token.substring(0, 20)}...` : 'missing')

    if (token) {
      // 使用 Bearer 认证方案
      config.headers.Authorization = token
      // console.log('[Request] Authorization header added')
    } else {
      // console.warn('[Request] No access token available, request will be sent without Authorization header')
    }

    // 如果用户已登录，添加 member_id header
    if (memberId) {
      config.headers['MemberId'] = memberId
      // console.log('[Request] member_id header added:', memberId)
    }

    // 添加时间戳防止缓存（可选）
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      }
    }

    return config
  },
  (error) => {
    // 隐藏 loading（需要判断配置）
    if (!error.config?.hideLoading) {
      hideLoading()
    }
    // console.error('[Request] Request error:', error)
    return Promise.reject(error)
  }
)

// 是否正在刷新 token
let isRefreshing = false
// 待重试的请求队列
let refreshSubscribers = []

/**
 * 添加请求到刷新队列
 * @param {Function} callback - 刷新成功后的回调
 */
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback)
}

/**
 * 刷新成功后，重试所有待处理的请求
 * @param {string} token - 新的 access token
 */
function onRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

/**
 * 刷新失败后，拒绝所有待处理的请求
 */
function onRefreshFailed() {
  refreshSubscribers = []
}

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    // 隐藏 loading（需要判断配置）
    if (!response.config?.hideLoading) {
      hideLoading()
    }

    const { data, config } = response
    // console.log('[Response] Data:', data)

    // 后端统一返回格式：{ code: 0, error: "success", data: {} }
    // code: 0 表示成功，非 0 表示失败
    // error: 错误信息描述，成功时为 "success" 或空字符串 ""
    // data: 响应数据，失败时可省略
    const { code, error: errorMsg, data: responseData } = data

    // 调试日志
    // console.log('[Response] URL:', config.url)
    // console.log('[Response] Code:', code)
    // console.log('[Response] Error:', errorMsg)

    // 成功：code === 0
    if (code === 0 || !code) {
      return responseData
    }

    // 其他错误：构造包含 code 的错误对象
    // console.error('[Response] Error - code:', code, 'message:', errorMsg)

    const error = new Error(errorMsg || 'UNKNOWN_ERROR')
    error.code = code
    error.error = errorMsg // 错误消息
    error.data = responseData // 附加完整的 data 字段，业务层可以访问额外信息

    // 用户被封禁：code === SERVER_ERROR_CODE.FORBIDDEN
    if (code === SERVER_ERROR_CODE.FORBIDDEN) {
      handleUserBanned()
    } else if (!config.hideError) {
      showToast(errorMsg || t('common.request.requestError'))
    }

    return Promise.reject(error)
  },
  async (error) => {
    // 隐藏 loading（需要判断配置）
    if (!error.config?.hideLoading) {
      hideLoading()
    }

    // console.error('[Response Error]:', error)

    // 网络错误
    if (!error.response) {
      // console.error('[Response Error] Network error - no response received')
      showToast(t('common.request.networkError'))
      return Promise.reject(error)
    }

    const { status, data, config } = error.response
    // console.error('[Response Error] HTTP Status:', status)
    // console.error('[Response Error] Response Data:', data)

    // HTTP 状态码错误处理
    let message = data?.error || error.message

    switch (status) {
      case 400:
        message = message || t('common.request.badRequest')
        break
      case 401: {
        // console.error('[Response Error] 401 Unauthorized')

        const userStore = useUserStore()

        // 如果用户已登录，尝试刷新 token
        if (userStore.checkLoginStatus()) {
          // console.log('[Response Error] Access token expired, attempting to refresh')

          // 如果不是刷新接口本身
          if (!config.url.includes('/auth/v1/refresh')) {
            // 避免多个请求同时刷新 token
            if (!isRefreshing) {
              isRefreshing = true

              try {
                // 刷新 token（其实这里不 try catch 也可以，因为 userStore.refreshAccessToken 内部已经处理了错误）
                const refreshSuccess = await userStore.refreshAccessToken()

                if (refreshSuccess) {
                  const newToken = userStore.accessToken
                  // console.log('[Response Error] Token refreshed successfully, retrying request')

                  // 通知所有等待的请求
                  onRefreshed(newToken)

                  // 重试原请求
                  config.headers.Authorization = newToken
                  // 这里要 return，否则会继续执行后面的代码，导致 Toast 异常
                  // 如： 401 之后 ——> 静默刷新 Token ——> 重试请求成功 ——> 因为没有return，显示 401 Toast
                  return request(config)
                } else {
                  // 刷新失败，清除登录状态
                  // console.log('[Response Error] Token refresh failed, logging out')
                  showToast({
                    message: t('common.request.tokenExpired'),
                    onClose: () => {
                      onRefreshFailed()
                      userStore.clearLoginState()

                      // 触发登录弹窗
                      showLoginModal({ source: 'token-refresh-failed' })
                    }
                  })
                }
              } catch (refreshError) {
                // console.error('[Response Error] Token refresh error:', refreshError)
                showToast({
                  message: t('common.request.tokenExpired'),
                  onClose: () => {
                    onRefreshFailed()
                    userStore.clearLoginState()

                    // 触发登录弹窗
                    showLoginModal({ source: 'token-refresh-failed' })
                  }
                })
              } finally {
                isRefreshing = false
              }
            } else {
              // 正在刷新中，将请求加入队列
              // console.log('[Response Error] Token refresh in progress, queueing request')
              return new Promise((resolve) => {
                subscribeTokenRefresh((token) => {
                  config.headers.Authorization = token
                  resolve(request(config))
                })
              })
            }
          } else {
            // 刷新接口本身失败，清除登录状态
            // console.log('[Response Error] Refresh token expired, logging out')
            userStore.clearLoginState()

            // 触发登录弹窗
            if (config.showLoginModal) {
              showLoginModal({ source: 'refresh-api-failed' })
            }
          }
        } else {
          // 用户未登录，触发登录弹窗
          // console.log('[Response Error] Not logged in, showing login dialog')
          userStore.clearLoginState()
          showLoginModal({ source: 'unauthorized' })
        }

        message = message || t('common.request.unauthorized')
        break
      }
      case 403: {
        // 用户被封禁，显示封禁提示弹窗
        handleUserBanned()
        // 不显示 Toast，避免重复提示
        return Promise.reject(error)
      }
      case 404:
        message = message || t('common.request.notFound')
        break
      case 500:
        message = message || t('common.request.serverError')
        break
      case 502:
        message = message || t('common.request.gatewayError')
        break
      case 503:
        message = message || t('common.request.serviceUnavailable')
        break
      case 504:
        message = message || t('common.request.gatewayTimeout')
        break
      default:
        message = message || `${t('common.request.requestError')} ${status}`
    }

    // 显示错误提示（可以通过配置关闭）
    if (!config?.hideError) {
      showToast(message)
    }

    return Promise.reject(error)
  }
)

// 导出封装的请求方法
export default request

// 便捷方法
export const get = (url, params, config = {}) => {
  return request({
    method: 'get',
    url,
    params,
    ...config,
  })
}

export const post = (url, data, config = {}) => {
  return request({
    method: 'post',
    url,
    data,
    ...config,
  })
}

export const put = (url, data, config = {}) => {
  return request({
    method: 'put',
    url,
    data,
    ...config,
  })
}

export const del = (url, params, config = {}) => {
  return request({
    method: 'delete',
    url,
    params,
    ...config,
  })
}

export const patch = (url, data, config = {}) => {
  return request({
    method: 'patch',
    url,
    data,
    ...config,
  })
}

// 文件上传
export const upload = (url, formData, config = {}) => {
  return request({
    method: 'post',
    url,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  })
}

// 下载文件
export const download = (url, params, filename, config = {}) => {
  return request({
    method: 'get',
    url,
    params,
    responseType: 'blob',
    ...config,
  }).then((response) => {
    const blob = new Blob([response])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename || 'download'
    link.click()
    URL.revokeObjectURL(link.href)
  })
}
